import React from 'react';
import { ListOfSportsZod } from 'src/domains/common/configFeatures/ConfigFeatures';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { z, ZodError } from 'zod';

const getSeoContent = (
    router: StarRouter,
    configComponents: ConfigComponents
): { title: string; description: string } | null => {
    const currentView = router.currentView;

    if (currentView === null) {
        return null;
    }

    const name = currentView.name;

    if (
        name === 'homepage' ||
        name === 'inplay' ||
        name === 'virtuals' ||
        name === 'casino' ||
        name === 'live-casino' ||
        name === 'sports' ||
        name === 'promoted-events'
    ) {
        const seoMeta = configComponents.config.seoMeta;

        if (seoMeta === null) {
            return null;
        }

        return seoMeta[name];
    }

    if (currentView.name === 'sport') {
        const seoMeta = configComponents.config.seoMetaSport;

        if (seoMeta === null) {
            return null;
        }

        try {
            const id = ListOfSportsZod.parse(currentView.id);
            return seoMeta[id] ?? null;
        } catch (e) {
            const message =
                e instanceof ZodError ? `Could not parse route ID for SEO meta sport: ${e.flatten().fieldErrors}` : e;
            console.error(message);
            return null;
        }
    }

    if (currentView.name === 'sport-special' && configComponents.config.seoMetaSpecialSports !== null) {
        try {
            const { id: routeId } = z
                .object({
                    id: z.string(),
                })
                .parse(currentView);

            const data = configComponents.config.seoMetaSpecialSports.find((specialSport) =>
                Object.hasOwn(specialSport, routeId)
            );

            if (data === undefined) {
                return null;
            }

            return data[routeId] ?? null;
        } catch (e) {
            const message =
                e instanceof ZodError
                    ? `Could not parse current view for SEO meta sport-special: ${e.flatten().fieldErrors}`
                    : e;
            console.error(message);
            return null;
        }
    }

    return null;
};

export const renderTitle = (router: StarRouter, configComponents: ConfigComponents): Array<React.ReactElement> => {
    const content = getSeoContent(router, configComponents);

    if (content !== null) {
        return [
            <title
                data-test='title'
                key='title'
            >
                {content.title}
            </title>,
            <meta
                data-test='description'
                key='description'
                name='description'
                content={content.description}
            />,
        ];
    }

    const text = configComponents.config.headerMetaTitleName;
    return [<title key='title'>{text}</title>];
};
